import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Card, LinearProgress, Link, Typography, Alert, Button, Skeleton, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import PlansAndBillingService from 'src/services/plans_and_billing';
import { UsageData, BillingCardsProps } from 'src/types/billing';

export function BillingCards({
  onUpgrade,
  usageData: propUsageData,
  loading: propLoading = false,
  error: propError = null,
  onRetry
}: BillingCardsProps) {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';

  // Local state for when data is not provided via props
  const [localUsageData, setLocalUsageData] = useState<UsageData | null>(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Use props if provided, otherwise use local state
  const usageData = propUsageData !== undefined ? propUsageData : localUsageData;
  const loading = propLoading || localLoading;
  const error = propError || localError;

  // Fetch usage data if not provided via props
  useEffect(() => {
    if (propUsageData === undefined && !localUsageData && !localLoading) {
      fetchUsageData();
    }
  }, [propUsageData, localUsageData, localLoading]);

  const fetchUsageData = async () => {
    try {
      setLocalLoading(true);
      setLocalError(null);
      const response = await PlansAndBillingService.getCurrentUsage();
      setLocalUsageData(response);
    } catch (err) {
      console.error('Error fetching usage data:', err);
      setLocalError('Failed to load usage information');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      fetchUsageData();
    }
  };

  // Default values for when data is not available
  const creditsUsed = usageData?.current_usage?.used_credits || 0;
  const creditsTotal = usageData?.current_usage?.total_credits || 0;
  const dailyCreditsUsed = usageData?.current_usage?.daily_credits_used || 0;
  const dailyCreditsTotal = usageData?.current_usage?.daily_credits || 0;
  const dailyCreditsRemaining = usageData?.current_usage?.daily_credits_remaining || 0;
  const monthlyCreditsRemaining = usageData?.current_usage?.remaining_credits || 0;
  const totalRemainingCredits = usageData?.current_usage?.total_remaining_credits || 0;
  const usagePercentage = usageData?.current_usage?.usage_percentage || 0;
  const dailyUsagePercentage = dailyCreditsTotal > 0 ? (dailyCreditsUsed / dailyCreditsTotal) * 100 : 0;
  const phoneLines = 3; // This could be fetched from a separate endpoint
  const renewalDate = usageData?.subscription?.expiry_date
    ? new Date(usageData.subscription.expiry_date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric'
      })
    : 'Next billing cycle';

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 2, sm: 3 },
          flexWrap: 'wrap',
          mt: { xs: 3, sm: 4, md: 5 },
          mb: { xs: 3, sm: 4, md: 5 },
        }}
      >
        <Card
          sx={{
            p: { xs: 2, sm: 3 },
            flex: 1,
            minWidth: { xs: '100%', sm: 340 },
            backgroundColor: darkMode
              ? theme.palette.background.paper
              : '#F8F8F8',
            color: darkMode ? theme.palette.text.primary : '#111827',
            border: darkMode ? `1px solid ${theme.palette.divider}` : 'none',
            transition: 'all 0.3s ease',
          }}
        >
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="rectangular" height={24} sx={{ my: 2, borderRadius: 3 }} />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="80%" />
        </Card>
        <Card
          sx={{
            p: { xs: 2, sm: 3 },
            flex: 1,
            minWidth: { xs: '100%', sm: 340 },
            borderRadius: 3,
            backgroundColor: darkMode
              ? theme.palette.background.paper
              : '#F8F8F8',
            color: darkMode ? theme.palette.text.primary : '#111827',
            border: darkMode ? `1px solid ${theme.palette.divider}` : 'none',
            transition: 'all 0.3s ease',
          }}
        >
          <Skeleton variant="text" width="70%" height={32} />
          <Skeleton variant="text" width="100%" />
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 2, sm: 3 },
          flexWrap: 'wrap',
          mt: { xs: 3, sm: 4, md: 5 },
          mb: { xs: 3, sm: 4, md: 5 },
        }}
      >
        <Card
          sx={{
            p: { xs: 2, sm: 3 },
            flex: 1,
            minWidth: { xs: '100%', sm: 340 },
            backgroundColor: darkMode
              ? theme.palette.background.paper
              : '#F8F8F8',
            color: darkMode ? theme.palette.text.primary : '#111827',
            border: darkMode ? `1px solid ${theme.palette.divider}` : 'none',
            transition: 'all 0.3s ease',
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={handleRetry} size="small">
            Retry
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 2, sm: 3 },
        flexWrap: 'wrap',
        mt: { xs: 3, sm: 4, md: 5 },
        mb: { xs: 3, sm: 4, md: 5 },
      }}
    >
      <Card
        sx={{
          p: { xs: 2, sm: 3 },
          flex: 1,
          minWidth: { xs: '100%', sm: 340 },
          backgroundColor: darkMode
            ? theme.palette.background.paper
            : '#F8F8F8',
          color: darkMode ? theme.palette.text.primary : '#111827',
          border: darkMode ? `1px solid ${theme.palette.divider}` : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            gap: { xs: 2, sm: 5 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              fontFamily={'Space Grotesk, sans-serif'}
              sx={{
                fontSize: { xs: '16px', sm: '18px', md: '20px' },
                color: darkMode ? theme.palette.text.primary : '#111827',
              }}
            >
              <AutoAwesomeIcon
                sx={{
                  fontSize: { xs: 18, sm: 20 },
                  mr: 1,
                  color: theme.palette.primary.main,
                }}
              />{' '}
              Credits
            </Typography>
            <HelpOutlineIcon
              sx={{
                fontSize: { xs: 12, sm: 14 },
                color: darkMode ? theme.palette.text.secondary : '#6B7280',
              }}
            />
          </Box>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{
              fontSize: { xs: '14px', sm: '16px' },
              color: darkMode ? theme.palette.text.primary : '#111827',
            }}
          >
            {totalRemainingCredits.toLocaleString()} remaining
          </Typography>
        </Box>

        {/* Daily Credits Section */}
        {dailyCreditsTotal > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                Daily Credits (resets at midnight UTC)
              </Typography>
              <Chip
                label={`${dailyCreditsUsed}/${dailyCreditsTotal}`}
                size="small"
                color={dailyUsagePercentage > 80 ? 'error' : dailyUsagePercentage > 60 ? 'warning' : 'success'}
              />
            </Box>
            <Box sx={{ position: 'relative', mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={100}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: darkMode ? theme.palette.grey[800] : '#E5E7EB',
                  '& .MuiLinearProgress-bar': {
                    opacity: 0,
                  },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '100%',
                  width: `${Math.min(dailyUsagePercentage, 100)}%`,
                  borderRadius: 3,
                  pointerEvents: 'none',
                  background: dailyUsagePercentage > 80
                    ? 'linear-gradient(90deg, #f44336 0%, #ff9800 100%)'
                    : dailyUsagePercentage > 60
                    ? 'linear-gradient(90deg, #ff9800 0%, #ffc107 100%)'
                    : 'linear-gradient(90deg, #1976d2 0%, #43a047 100%)',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {dailyCreditsRemaining} daily credits remaining
            </Typography>
          </Box>
        )}

        {/* Monthly Credits Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              Monthly Credits
            </Typography>
            <Chip
              label={`${creditsUsed}/${creditsTotal}`}
              size="small"
              color={usagePercentage > 80 ? 'error' : usagePercentage > 60 ? 'warning' : 'success'}
            />
          </Box>
          <Box sx={{ position: 'relative', mb: 1 }}>
            <LinearProgress
              variant="determinate"
              value={100}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: darkMode ? theme.palette.grey[800] : '#E5E7EB',
                '& .MuiLinearProgress-bar': {
                  opacity: 0,
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: `${Math.min(usagePercentage, 100)}%`,
                borderRadius: 3,
                pointerEvents: 'none',
                background: usagePercentage > 80
                  ? 'linear-gradient(90deg, #f44336 0%, #ff9800 100%)'
                  : usagePercentage > 60
                  ? 'linear-gradient(90deg, #ff9800 0%, #ffc107 100%)'
                  : 'linear-gradient(90deg, #1976d2 0%, #43a047 100%)',
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {monthlyCreditsRemaining} monthly credits remaining
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: darkMode ? theme.palette.text.primary : 'black',
            mb: 3,
            fontSize: { xs: 14, sm: 16 },
            fontWeight: 100,
          }}
        >
          {dailyCreditsTotal > 0 ? (
            <>
              <strong>Daily credits are consumed first</strong>, then monthly credits.
              Daily credits reset at midnight UTC. Your monthly credits will renew on{' '}
              {renewalDate}.
            </>
          ) : (
            <>
              {usagePercentage > 80 ? 'High usage' : usagePercentage > 60 ? 'Medium usage' : 'Low usage'} - {usagePercentage.toFixed(1)}% used.
              Your monthly credits will renew on {renewalDate}.
            </>
          )}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: darkMode ? theme.palette.text.primary : 'black',
            mb: 1,
            fontSize: { xs: 13, sm: 14, md: 16 },
          }}
        >
          <Link
            href="#"
            underline="always"
            sx={{
              color: darkMode ? theme.palette.primary.main : 'black',
              fontWeight: 400,
              textDecoration: 'underline',
              '&:hover': {
                color: theme.palette.primary.dark,
              },
            }}
          >
            Manage your payment preferences
          </Link>{' '}
          or change your plan below.{' '}
          <Link
            href="#"
            underline="always"
            sx={{
              color: darkMode ? theme.palette.primary.main : 'black',
              fontWeight: 400,
              textDecoration: 'underline',
              '&:hover': {
                color: theme.palette.primary.dark,
              },
            }}
          >
            Buy more credits here.
          </Link>
        </Typography>
      </Card>
      {/* Dedicated phone line card */}
      <Card
        sx={{
          p: { xs: 2, sm: 3 },
          flex: 1,
          minWidth: { xs: '100%', sm: 340 },
          borderRadius: 3,
          backgroundColor: darkMode
            ? theme.palette.background.paper
            : '#F8F8F8',
          color: darkMode ? theme.palette.text.primary : '#111827',
          border: darkMode ? `1px solid ${theme.palette.divider}` : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Typography
          mb={1}
          variant="h6"
          fontWeight={700}
          fontFamily={'Space Grotesk, sans-serif'}
          sx={{
            fontSize: { xs: '16px', sm: '18px', md: '20px' },
            color: darkMode ? theme.palette.text.primary : '#111827',
          }}
        >
          Dedicated phone line
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: darkMode ? theme.palette.text.primary : '#000',
            fontSize: { xs: 14, sm: 15, md: 16 },
            '& b': {
              color: theme.palette.primary.main,
              fontWeight: 600,
            },
          }}
        >
          You currently have <b>{phoneLines} dedicated phone lines</b>.{' '}
          <Link href="#">Add another for just $9/month</Link>
        </Typography>
      </Card>
    </Box>
  );
}
